import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Bell, BellOff, ChevronDown, Clock } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Türkiye destek saatlerini kullanıcının yerel saatine çevir
// Türkiye = UTC+3 (Europe/Istanbul)
function getSupportHoursInLocalTime() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzName = tz || 'Unknown';

    // Referans gün: bugün, saat 09:00 ve 18:00 Türkiye (UTC+3)
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();

    // UTC offset'i hesapla: 09:00 TR = 06:00 UTC
    const startUTC = new Date(Date.UTC(year, month, day, 6, 0));   // 09:00 TR
    const endUTC   = new Date(Date.UTC(year, month, day, 15, 0));  // 18:00 TR

    const fmt = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    const localStart = fmt(startUTC);
    const localEnd   = fmt(endUTC);

    // Kullanıcının UTC offset'i
    const offsetMin = -today.getTimezoneOffset(); // pozitif = UTC+ 
    const offsetH = Math.floor(Math.abs(offsetMin) / 60);
    const offsetM = Math.abs(offsetMin) % 60;
    const sign = offsetMin >= 0 ? '+' : '-';
    const offsetStr = `UTC${sign}${String(offsetH).padStart(2,'0')}:${String(offsetM).padStart(2,'0')}`;

    const isTurkey = tz && (tz.includes('Istanbul') || tz.includes('Turkey') || offsetMin === 180);
    const locationLabel = isTurkey ? '' : ` (${offsetStr})`;

    return { localStart, localEnd, offsetStr, tzName, locationLabel, isTurkey };
  } catch {
    return { localStart: '09:00', localEnd: '18:00', offsetStr: 'UTC+03:00', tzName: 'Turkey', locationLabel: ' (UTC+03:00)', isTurkey: true };
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

const ChatWidget = ({ token, userName }) => {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const supportHours = getSupportHoursInLocalTime();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchMessages = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/chat/my-messages`, { headers });
      setMessages(res.data);
      setUnread(0);
    } catch {}
  }, [token]);

  const fetchUnread = useCallback(async () => {
    if (!token || open) return;
    try {
      const res = await axios.get(`${API}/chat/unread-count`, { headers });
      setUnread(res.data.count || 0);
    } catch {}
  }, [token, open]);

  useEffect(() => {
    if (open) {
      fetchMessages();
    }
  }, [open, fetchMessages]);

  // Poll for new messages
  useEffect(() => {
    pollRef.current = setInterval(() => {
      if (open) fetchMessages();
      else fetchUnread();
    }, 4000);
    fetchUnread();
    return () => clearInterval(pollRef.current);
  }, [open, fetchMessages, fetchUnread]);

  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // Check push permission
  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.getRegistration('/sw.js').then(reg => {
        if (reg) {
          reg.pushManager.getSubscription().then(sub => {
            setPushEnabled(!!sub);
          });
        }
      });
    }
  }, []);

  const registerPush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    setPushLoading(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setPushLoading(false); return; }

      let reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) reg = await navigator.serviceWorker.register('/sw.js');

      const { data } = await axios.get(`${API}/chat/vapid-public-key`);
      const appServerKey = urlBase64ToUint8Array(data.public_key);

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appServerKey
      });

      await axios.post(`${API}/chat/push-subscribe`, {
        subscription: sub.toJSON(),
        role: 'customer_admin'
      }, { headers });

      setPushEnabled(true);
    } catch (e) {
      console.error('Push register error:', e);
    }
    setPushLoading(false);
  };

  const unregisterPush = async () => {
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
      }
      setPushEnabled(false);
    } catch {}
    setPushLoading(false);
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/my-messages`, { content: input.trim() }, { headers });
      setMessages(prev => [...prev, res.data]);
      setInput('');
    } catch {}
    setSending(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Floating Button */}
      <button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen(v => !v)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-110"
        style={{ background: 'linear-gradient(135deg, #6d28d9, #4f46e5)' }}
      >
        {open ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6 text-white" />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </>
        )}
      </button>

      {/* Chat Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-6 z-50 w-80 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{ height: '460px', background: '#fff', border: '1px solid #e5e7eb' }}
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #6d28d9, #4f46e5)' }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white text-sm font-semibold">COP 31 Administration</p>
                <p className="text-white/70 text-xs">{tr ? 'Admin ile sohbet' : 'Chat with admin'}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={pushEnabled ? unregisterPush : registerPush}
                disabled={pushLoading}
                className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                title={pushEnabled ? (tr ? 'Bildirimleri kapat' : 'Disable notifications') : (tr ? 'Bildirimleri aç' : 'Enable notifications')}
              >
                {pushEnabled
                  ? <Bell className="w-4 h-4 text-yellow-300" />
                  : <BellOff className="w-4 h-4 text-white/60" />
                }
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/20 transition-colors">
                <ChevronDown className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ background: '#f8fafc' }}>
            {/* Welcome card */}
            <div className="flex justify-start">
              <div className="max-w-[88%] rounded-2xl rounded-tl-sm px-3 py-2.5 text-sm shadow-sm bg-white border border-gray-100">
                <p className="font-semibold text-purple-700 text-xs mb-1">COP 31 Administration</p>
                <p className="text-gray-700">
                  {tr ? 'Merhaba! 👋 COP 31 Antalya portalına hoş geldiniz.' : 'Hello! 👋 Welcome to the COP 31 Antalya portal.'}
                </p>
                <p className="text-gray-600 text-xs mt-1.5">
                  {tr
                    ? 'Standınız, siparişleriniz veya etkinlikle ilgili her konuda buradayız.'
                    : 'We\'re here for any questions about your stand, orders, or the event.'}
                </p>
                <div className="mt-2 pt-2 border-t border-gray-100 flex items-start gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0 mt-0.5"></span>
                  <div className="text-xs text-gray-500">
                    <span>{tr ? 'Destek saatleri:' : 'Support hours:'} </span>
                    <strong>
                      {tr ? 'Hft İçi' : 'Mon–Fri'}{' '}
                      {supportHours.localStart} – {supportHours.localEnd}
                    </strong>
                    {!supportHours.isTurkey && (
                      <span className="text-gray-400"> {supportHours.locationLabel}</span>
                    )}
                    {supportHours.isTurkey && (
                      <span className="text-gray-400"> (UTC+03:00)</span>
                    )}
                    {!supportHours.isTurkey && (
                      <div className="text-gray-400 mt-0.5">
                        {tr ? '(Türkiye 09:00–18:00 saatine göre)' : '(Turkey time: 09:00–18:00)'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {messages.length === 0 && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-tl-sm px-3 py-2 text-sm bg-white border border-gray-100 shadow-sm">
                  <p className="text-gray-600 text-xs">
                    {tr
                      ? 'Aşağıya mesajınızı yazın, ekibimiz en kısa sürede yanıtlayacak. 🙏'
                      : 'Type your message below and our team will respond as soon as possible. 🙏'}
                  </p>
                </div>
              </div>
            )}

            {messages.map(msg => (
              <div
                key={msg.id}
                className={`flex ${msg.sender_role === 'ultra_admin' ? 'justify-start' : 'justify-end'}`}
              >
                <div
                  className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                    msg.sender_role === 'ultra_admin'
                      ? 'bg-white text-gray-800 rounded-tl-sm border border-gray-100'
                      : 'text-white rounded-tr-sm'
                  }`}
                  style={msg.sender_role !== 'ultra_admin' ? { background: 'linear-gradient(135deg, #6d28d9, #4f46e5)' } : {}}
                >
                  <p style={{ wordBreak: 'break-word' }}>{msg.content}</p>
                  <p className={`text-[10px] mt-0.5 ${msg.sender_role === 'ultra_admin' ? 'text-gray-400' : 'text-white/60'} text-right`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-100 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder={tr ? 'Mesajınızı yazın... (Enter = Gönder)' : 'Type a message... (Enter = Send)'}
                rows={2}
                className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
                style={{ maxHeight: '80px' }}
              />
              <button
                data-testid="chat-send-btn"
                onClick={sendMessage}
                disabled={sending || !input.trim()}
                className="w-9 h-9 rounded-xl flex items-center justify-center transition-all disabled:opacity-40 flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #6d28d9, #4f46e5)' }}
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
